<script lang="ts" setup>
    import type { ITestimonial } from '@/types/LP/testimonial';
    import { computed, ref } from 'vue';

    interface IProps {
        review: ITestimonial
    }
    const props = defineProps<IProps>();
    const { t } = useI18n()

    const isExpanded = ref(false);
    const CHAR_LIMIT = 120;

    const isLongText = computed(() => props.review.content.length > CHAR_LIMIT);

    const toggleReadMore = () => {
        isExpanded.value = !isExpanded.value;
    };
</script>

<template>
    <div class="card-review">
        <div class="card-review__header">
            <div class="card-review__user">
                <div class="card-review__avatar-container">
                    <img 
                        v-if="review.profile_picture" 
                        :src="review.profile_picture" 
                        :alt="review.name" 
                        class="card-review__avatar ratio-item"
                    >
                    <img 
                        v-else 
                        src="/images/landing-page/samurai-icon.svg" 
                        class="card-review__avatar-fallback ratio-item"
                    >
                </div>
                <div class="card-review__user-meta">
                <h3 class="card-review__user-name">{{ review.name }}</h3>
                <p class="card-review__user-role">{{ review.position }}</p>
                </div>
            </div>
        </div>
        <div class="card-review__content">
            <div
                class="card-review__text"
                :class="{ '-is-expanded': isExpanded }"
            >
                {{ review.content }}
            </div>
            <button
                v-if="isLongText"
                @click="toggleReadMore"
                class="card-review__read-more"
            >
                {{ isExpanded ? t('Read Less') : t('Read More') }}
            </button>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.card-review {
    background: #FFFFFF;
    border: 1px solid #FFFFFF;
    display: flex;
    flex-direction: column;
    transition: all 0.3s ease;
    @include imageRatio(62.91 62.91, 50 50, true, cover, 'avatar');
    @include vwUnit('min-height', 207, 200);
    @include vwUnit('padding', 28, 24);
    @include vwUnit('border-radius', 8.58, 8);
    @include vwUnit('gap', 24, 20);
    &__header {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }
    &__user {
        display: flex;
        align-items: center;
        width: 100%;
        @include vwUnit('gap', 11, 11);
    }
    &__avatar-container {
        overflow: hidden;
        border-radius: 50%;
        background:#211F1F;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    &__avatar-fallback {
        max-width: 32px;
        height: auto;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }
    &__user-meta {
        min-width: 0;
    }
    &__user-name {
        margin-bottom: 0px;
        color: #2E263DE5;
        font-weight: 600;
        line-height: 1;
        @include vwUnit('font-size', 28, 26);
        letter-spacing: -0.02em;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    &__user-role {
        color: #8D8D8D;
        @include vwUnit('font-size', 18, 16);
        line-height: 1.7;
        margin-bottom: 0px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    &__content {
        flex-grow: 1;
        display: flex;
        flex-direction: column;
        gap: 8px;
    }
    &__text {
        font-family: $primary-font-light;
        color: #404040;
        @include vwUnit('font-size', 18, 18);
        line-height: 1.6;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
        overflow: hidden;
        transition: max-height 0.4s ease;
        max-height: calc(1.6em * 2);
        &.-is-expanded {
            -webkit-line-clamp: unset;
            max-height: 1000px;
        }
    }
    &__read-more {
        align-self: flex-start;
        background: none;
        border: none;
        padding: 0;
        color: color(primary);
        cursor: pointer;
        @include vwUnit('font-size', 14, 14);
        font-weight: 600;
        text-decoration: underline;
        transition: color 0.2s;
        &:hover {
            text-decoration: none;
        }
    }
}
</style>
